import { computed, ref } from 'vue'
import axios from 'axios'
import { Capacitor } from '@capacitor/core'
import { version as localVersion } from '../../package.json'
import tools from '@/composables/tools'

export const APP_VERSION_SLUG = 'cli'

export interface FrontVersionInfo {
  app: string
  version_latest: string | null
  version_min: string | null
  url_actualizacion: string | null
  store_url_ios: string | null
  store_url_android: string | null
  mensaje: string | null
  activo: boolean
  version_cliente: string | null
  estado: 'ok' | 'aviso' | 'forzar' | null
  force: boolean
  aviso: boolean
}

const POLL_MS = 10 * 60 * 1000
const SNOOZE_PREFIX = 'app_version_snooze:'

const isOpen = ref(false)
const info = ref<FrontVersionInfo | null>(null)
const presentedFor = ref<string | null>(null)
let pollTimer: ReturnType<typeof setInterval> | null = null
let started = false

function snoozeKey(latest: string): string {
  return SNOOZE_PREFIX + latest
}

function isSnoozed(latest: string | null): boolean {
  if (!latest) {
    return false
  }
  try {
    return sessionStorage.getItem(snoozeKey(latest)) === '1'
  } catch {
    return false
  }
}

function setSnoozed(latest: string | null): void {
  if (!latest) {
    return
  }
  try {
    sessionStorage.setItem(snoozeKey(latest), '1')
  } catch {
    /* noop */
  }
}

function whatsappPhone(): string {
  return String(process.env.VUE_APP_WHATSAPP_HELP || '+34604048881').replace(/[^\d+]/g, '')
}

function logAccion(accion: string): void {
  const latest = info.value?.version_latest ?? '?'
  const estado = info.value?.estado ?? '?'
  tools().setLog(
    `Aviso versión desactualizada: ${accion} · local=${localVersion} · latest=${latest} · estado=${estado}`,
    'info'
  )
}

export function useAppVersionCheck() {
  const force = computed(() => Boolean(info.value?.force))

  const mensaje = computed(() => {
    const custom = info.value?.mensaje?.trim()
    if (custom) {
      return custom
    }
    const latest = info.value?.version_latest ?? ''
    return latest
      ? `Estás usando la versión ${localVersion} y hay una más reciente (${latest}). Debes actualizar la aplicación para seguir usándola con normalidad.`
      : `Estás usando la versión ${localVersion}, que ya no está al día. Debes actualizar la aplicación.`
  })

  async function check(): Promise<void> {
    try {
      const response = await axios.get(
        `/front/versiones/${APP_VERSION_SLUG}`,
        {
          params: { version: localVersion },
          skipErrorToast: true,
        }
      )
      const data = response.data as FrontVersionInfo

      if (!data?.activo || !data.aviso) {
        isOpen.value = false
        info.value = data ?? null
        return
      }

      info.value = data

      if (!data.force && isSnoozed(data.version_latest)) {
        isOpen.value = false
        return
      }

      const firma = `${data.estado}:${data.version_latest}:${localVersion}`
      const yaPresentado = presentedFor.value === firma
      isOpen.value = true
      if (!yaPresentado) {
        presentedFor.value = firma
        logAccion('mostrado')
      }
    } catch {
      /* catálogo no disponible: no molestar */
    }
  }

  function start(): void {
    if (started) {
      return
    }
    started = true
    void check()
    pollTimer = setInterval(() => {
      void check()
    }, POLL_MS)

    document.addEventListener('visibilitychange', onVisibility)
  }

  function onVisibility(): void {
    if (document.visibilityState === 'visible') {
      void check()
    }
  }

  function stop(): void {
    if (pollTimer) {
      clearInterval(pollTimer)
      pollTimer = null
    }
    document.removeEventListener('visibilitychange', onVisibility)
    started = false
  }

  function resolveUpdateUrl(): string | null {
    const row = info.value
    if (!row) {
      return null
    }
    if (Capacitor.isNativePlatform()) {
      const platform = Capacitor.getPlatform()
      if (platform === 'ios' && row.store_url_ios) {
        return row.store_url_ios
      }
      if (platform === 'android' && row.store_url_android) {
        return row.store_url_android
      }
    }
    return row.url_actualizacion
  }

  function onActualizar(): void {
    logAccion('actualizar')
    const url = resolveUpdateUrl()
    if (url) {
      const opened = window.open(url, '_blank', 'noopener,noreferrer')
      if (!opened) {
        window.location.href = url
      }
      return
    }
    window.location.reload()
  }

  function onWhatsapp(): void {
    logAccion('whatsapp')
    const latest = info.value?.version_latest ?? ''
    const text = encodeURIComponent(
      `Hola, estoy en la app Galidancia versión ${localVersion}`
      + (latest ? ` y me pide actualizar a ${latest}` : ' y me indica que está desactualizada')
      + '. Necesito asistencia técnica para actualizar.'
    )
    const phone = whatsappPhone()
    const digits = phone.replace(/^\+/, '')
    window.location.href = `whatsapp://send?phone=${phone}&text=${text}`
    window.setTimeout(() => {
      window.open(`https://wa.me/${digits}?text=${text}`, '_blank', 'noopener,noreferrer')
    }, 600)
  }

  function onMasTarde(): void {
    if (force.value) {
      return
    }
    logAccion('mas_tarde')
    setSnoozed(info.value?.version_latest ?? null)
    isOpen.value = false
  }

  return {
    isOpen,
    info,
    force,
    mensaje,
    localVersion,
    start,
    stop,
    onActualizar,
    onWhatsapp,
    onMasTarde,
  }
}
